import { landingLocales, type LandingLocale } from './i18n';

/**
 * A corporate report ("memoria"), one per year, hosted on Google Drive.
 *
 * Only the four landing locales have their own file; every other language falls back to Spanish. Some
 * years point several locales at the same document because only one translation was produced.
 */
export type AnnualReport = {
	year: string;
	links: Record<LandingLocale, string>;
};

/**
 * OLDEST FIRST. The transparency page lists them in this order; the About page's rail runs the other
 * way and reverses a copy, so the order here is the data's, not either layout's.
 *
 * This is the single home for the list: it grows by one entry a year and is read from two pages, which
 * is exactly the shape that silently goes stale when it is written down twice.
 */
export const annualReports: AnnualReport[] = [
	{
		year: '2019',
		links: {
			es: 'https://drive.google.com/file/d/11GQHytfX-r3wfp0byrzFHUFdj1PMp--k/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1x3azVxsWLnP5C05dB09KNimFL3jKz55O/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11Cpzb_lpjr0qd9tsWnb_B54gkQq4kF2G/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1x3azVxsWLnP5C05dB09KNimFL3jKz55O/view?usp=drive_link',
		},
	},
	{
		year: '2020',
		links: {
			es: 'https://drive.google.com/file/d/15q7vIbEtyj1989wQ5tFwfLk8IZKC62D-/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/15kqDxfAWv11eTtg2DTK5-8ebjnhR6Pc4/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/15oEaqzlWivCO-NS34EEyYbBzArD8ftYV/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/15kqDxfAWv11eTtg2DTK5-8ebjnhR6Pc4/view?usp=sharing',
		},
	},
	{
		year: '2021',
		links: {
			es: 'https://drive.google.com/file/d/11O4oTYrNBTdmNDekO7tvKlcgnw5hpeaz/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/11M2V5DWFVpPZG9Ia0uULULCa4XQtCbLD/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11PGrTzQB2fX81iBRTlA-kqgFG8OKtQiw/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/11M2V5DWFVpPZG9Ia0uULULCa4XQtCbLD/view?usp=drive_link',
		},
	},
	{
		year: '2022',
		links: {
			es: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/11VvkRdC1ITXQziBFBHNXIB_aBbAJdSUV/view?usp=drive_link',
		},
	},
	{
		year: '2023',
		links: {
			es: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1qv4sgLYKJG4RD6yffdHeLkKXhg8rziTC/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1j1cQKVsPvsR1OzII8Mf3H_hrvYbJYG2B/view?usp=drive_link',
		},
	},
	{
		year: '2024',
		links: {
			es: 'https://drive.google.com/file/d/1r-kupm93JevZAhUESxsooHyFX_IdtwdP/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/1umRWzio8RDvMPS_n-_fY1pgWA6Xk1pWU/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1LlrnQgqv92pwOfgVCpac0NPkvielNab1/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1UzHzcW2E8sKQHKk-ivInpQb9OUq5ZAyN/view?usp=drive_link',
		},
	},
	{
		year: '2025',
		links: {
			es: 'https://drive.google.com/file/d/1FdmbDD-DzgmxwZbM1RaW98sBopiDGI0Y/view?usp=drive_link',
			en: 'https://drive.google.com/file/d/14OFpqkJDV09tkt0vyhw7Ul49CS9xfvQY/view?usp=drive_link',
			ca: 'https://drive.google.com/file/d/1NFaRjjpxaut8tRlccI93lhKBq3TnIaj4/view?usp=drive_link',
			it: 'https://drive.google.com/file/d/1vdbeR3xnRDZIeYSSYd3yKrp8pnJmLkIA/view?usp=drive_link',
		},
	},
];

/**
 * The document to open for a given UI language: its own if it is one of the four the reports exist in,
 * Spanish otherwise. The second fallback covers an entry that has the key but leaves it empty.
 */
export function annualReportUrl(links: AnnualReport['links'], language: string): string {
	const locale = (landingLocales as readonly string[]).includes(language) ? (language as LandingLocale) : 'es';
	return links[locale] || links.es;
}
